"use client";

import { useState } from "react";
import type { EventLogState } from "@/lib/sse/useSSE";
import { useTranslations } from "@/lib/compat/i18n";
import { ContractCapture, ContractAndInsuranceSignModals } from "@/components/scenes/act2/ContractCapture";
import { SplitScreen } from "@/components/scene/SplitScreen";
import { HANDSHAKE } from "@/lib/handshakes";
import { useUserActions } from "@/lib/userActions/UserActionsContext";
import type { SignParty } from "@/lib/sign/signerParties";
import { EnterpriseEvidence } from "@/components/scenes/shared/EnterpriseEvidence";

/**
 * ContractCaptureWithHandshake — extracted out of SceneRouter as part
 * of the M1 split. Rendered on T1 2.1, T2 4.5, T3 5.5 — the
 * contract-capture beat that's also handshake #3.
 *
 * Behaviour summary:
 *   1. Mounts in "ContractCapture" mode. The sign-doc modal chain
 *      (contract → insurance) is owned by ContractAndInsuranceSignModals
 *      sibling, which lives outside the inner swap so the modal isn't
 *      torn down when the handshake fires.
 *   2. When the user closes both modals, `onChainComplete` flips
 *      `userSigned`. With the handshake event already in the projection,
 *      the inner branch swaps to the SplitScreen handshake view.
 *   3. Optional CTA gate (`requireSignCta`) — surface a "Sign with your
 *      Orbitra ID" overlay before opening the contract modal. Used on
 *      T2 4.5.
 *   4. Optional Begin-LC-composition trigger — exposes a CTA that
 *      starts the LC composition progress bar at 0 → 15%. Used on T2
 *      4.5 only. The "started" flag persists via UserActionsContext so
 *      it survives the SceneRouter unmount/remount on Shift+N replay.
 */
export function ContractCaptureWithHandshake({
  ev,
  signParty,
  requireSignCta = false,
  showLcCompositionTrigger = false,
  signCtaActionKey,
  lcCompositionActionKey,
}: {
  ev: EventLogState;
  signParty: SignParty;
  requireSignCta?: boolean;
  showLcCompositionTrigger?: boolean;
  signCtaActionKey?: string;
  lcCompositionActionKey?: string;
}) {
  const tc = useTranslations("contractCaptureWithHandshake");
  const userActions = useUserActions();
  const handshakeFired = ev.splitScreenHandshakes.includes(HANDSHAKE.CONTRACT_CAPTURED);
  const [userSigned, setUserSigned] = useState(false);
  const lcAlreadyStarted = lcCompositionActionKey ? userActions.has(lcCompositionActionKey) : false;
  const [lcCompositionStarted, setLcCompositionStarted] = useState(lcAlreadyStarted);

  const inner = (handshakeFired && userSigned)
    ? (
      <SplitScreen
        number={3}
        caption={tc("splitCaption")}
        left={
          <div className="mt-6 grid place-items-center text-[12px] text-orbitra-paper">
            <p className="text-state-traced">{tc("contractCapturedLabel")}</p>
            <p className="mt-2 font-mono text-orbitra-muted">{tc("contractRoleSeller")}</p>
          </div>
        }
        right={
          <div className="mt-6 grid place-items-center text-[12px] text-orbitra-paper">
            <p className="text-state-traced">{tc("contractAppearsPipelineLabel")}</p>
            <p className="mt-2 font-mono text-orbitra-muted">{tc("contractRoleBuyer")}</p>
          </div>
        }
      />
    )
    : <ContractCapture
        contract={ev.contract}
        captured={!!ev.contract}
        corpDidSigned={userSigned}
        onBeginLcComposition={showLcCompositionTrigger ? () => {
          setLcCompositionStarted(true);
          if (lcCompositionActionKey) userActions.fire(lcCompositionActionKey);
        } : undefined}
        lcCompositionStarted={lcCompositionStarted}
      />;

  return (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 min-h-0">{inner}</div>
      {/* LC-composition progress card moved inside ContractCapture's right
       *  column on T2 4.5 (under Atlas Agent) per the latest amendment.
       *  The bottom-strip variant is retired. */}
      <ContractAndInsuranceSignModals
        contractRef={ev.contract?.id}
        onChainComplete={() => setUserSigned(true)}
        contractSigner={signParty.contract}
        insuranceSigner={signParty.insurance}
        contractPdf={signParty.contractPdf}
        insurancePdf={signParty.insurancePdf}
        requireCta={requireSignCta}
        ctaActionKey={signCtaActionKey}
      />

      <EnterpriseEvidence variant="contract-captured" />
    </div>
  );
}
